import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Pressable } from "react-native";
import { LegendList, type LegendListRef } from "@legendapp/list";
import { Card, Text, View } from "@src/components";
import type { WasteSenseMapSite, WasteSenseSite } from "../types";
import { countPotentialSiteIncidents, countPrioritySiteIncidents } from "../priority";
import { formatSiteHeading } from "../siteDisplay";
import { SiteLocationsMap } from "./SiteLocationsMap";

interface PriorityMapProps {
  sites: readonly WasteSenseSite[];
  /**
   * Marker entries for the interactive map. Optional — when omitted (or
   * empty) the component renders the list-only view and the map pane
   * shows a "no location data" placeholder.
   */
  mapSites?: readonly WasteSenseMapSite[];
  /**
   * Optional handler invoked when a user requests "open site details".
   * The detail screen scrolls to the matching `SiteIncidentLogs` block
   * so this is wired up purely for in-page navigation.
   */
  onOpenSite?: (assetId: string) => void;
  /**
   * External focus request — typically driven by the priority hero's
   * "Locate on map" button. Each new `{ assetId, key }` value (the
   * `key` lets the same `assetId` re-trigger focus) opens + activates
   * the matching row, scrolls the list to it, and pans the map. The
   * `key` lives in the parent so consecutive clicks on the same hero
   * row still re-focus rather than no-op.
   */
  focusRequest?: { assetId: string; key: number } | null;
}

/** Fixed viewport for the priority map and its companion list. */
const PRIORITY_MAP_HEIGHT = 540;

/**
 * Average list-item height. A collapsed card is roughly 80–96px; an
 * expanded card balloons past 220px. The estimate biases toward the
 * collapsed case because that's the steady state — `LegendList` will
 * remeasure expanded items as they open and shift the scrollbar.
 */
const ESTIMATED_ITEM_HEIGHT = 96;

/**
 * "Priority map" section — left-side site list (collapsible summary
 * metadata) paired with an interactive Leaflet map on the right.
 *
 * Both panes share a fixed height (`PRIORITY_MAP_HEIGHT`) so the
 * report doesn't stretch vertically with the site count. The list is
 * virtualized via `LegendList`; row state (expanded, active) is owned
 * by this parent so `recycleItems` can safely reuse row components.
 */
export function PriorityMap({ sites, mapSites, onOpenSite, focusRequest }: PriorityMapProps) {
  const [activeAssetId, setActiveAssetId] = useState<string | null>(null);
  const [openAssetId, setOpenAssetId] = useState<string | null>(null);
  const listRef = useRef<LegendListRef>(null);

  // Resolves *any* asset_id — primary OR secondary — to the site that
  // owns it. The priority hero passes an incident's `row.asset_id`,
  // which can be any camera on a multi-asset site, so we need to
  // accept the full asset namespace at the boundary and normalise to
  // the site's primary id (the one every other piece of state in this
  // component is keyed on: list rows, `WasteSenseMapSite.asset_id`,
  // `activeAssetId`, marker focus). Without this normalisation a hero
  // click on a secondary camera silently no-ops because nothing in the
  // primary-id state machine recognises the requested id.
  const siteByAssetId = useMemo(() => {
    const m = new Map<string, WasteSenseSite>();
    for (const site of sites) {
      for (const assetId of site.assets) {
        if (!m.has(assetId)) m.set(assetId, site);
      }
    }
    return m;
  }, [sites]);

  // Map asset_id → list index so a marker click can `scrollToIndex`
  // without scanning `sites` on every interaction. Indexed by the
  // *primary* asset id for each site, which is the id stored in
  // `activeAssetId`/`openAssetId` after `handleSelectFromMap`'s
  // normalisation; lookups for secondary asset ids go through
  // `resolvePrimaryAssetId` first.
  const indexByAssetId = useMemo(() => {
    const m = new Map<string, number>();
    sites.forEach((s, i) => {
      const id = primaryAssetId(s);
      if (!m.has(id)) m.set(id, i);
    });
    return m;
  }, [sites]);

  const resolvePrimaryAssetId = useCallback(
    (assetId: string): string => {
      const site = siteByAssetId.get(assetId);
      return site ? primaryAssetId(site) : assetId;
    },
    [siteByAssetId],
  );

  // Map asset_id → row-derived priority count, so the map popup can
  // render the same number the KPI strip and the priority hero use
  // (single source of truth — see `priority.ts`). Falls through to
  // `WasteSenseMapSite.priority_incidents` inside the map component
  // when an asset isn't represented in `sites` (shouldn't happen in
  // practice — `map_sites[]` is a subset of `sites[]`).
  const priorityCountByAssetId = useMemo(() => {
    const m = new Map<string, number>();
    for (const site of sites) {
      const count = countPrioritySiteIncidents(site);
      for (const assetId of site.assets) {
        m.set(assetId, count);
      }
    }
    return m;
  }, [sites]);

  const priorityCountFor = useCallback(
    (assetId: string): number | undefined => priorityCountByAssetId.get(assetId),
    [priorityCountByAssetId],
  );

  const handleToggle = useCallback((assetId: string) => {
    setOpenAssetId((current) => (current === assetId ? null : assetId));
    setActiveAssetId(assetId);
  }, []);

  /**
   * Marker → list direction: open + activate the corresponding row
   * AND scroll it into view in the virtualised list. We bias the
   * target position toward the top (`viewPosition: 0.1`) so the
   * just-opened row's expanded body is comfortably visible inside
   * the fixed-height pane.
   *
   * The incoming `assetId` may be a *secondary* camera id when the
   * caller is the priority hero (which forwards `row.asset_id`
   * verbatim). Map markers and our own list rows are keyed on the
   * site's *primary* asset id, so we normalise here at the boundary
   * and store/forward the primary id everywhere downstream.
   */
  const handleSelectFromMap = useCallback(
    (assetId: string) => {
      const primaryId = resolvePrimaryAssetId(assetId);
      setOpenAssetId(primaryId);
      setActiveAssetId(primaryId);
      const index = indexByAssetId.get(primaryId);
      if (index !== undefined) {
        listRef.current?.scrollToIndex({ index, animated: true, viewPosition: 0.1 });
      }
    },
    [indexByAssetId, resolvePrimaryAssetId],
  );

  /**
   * Normalise the *external* focus request before forwarding it to
   * the map. The hero passes the priority row's own asset_id, which
   * may not exist in `WasteSenseMapSite[]` (markers are keyed on each
   * site's primary asset). Without this rewrite the map's
   * `FocusController` would `points.find` against a secondary id,
   * miss, and the fly-to would silently no-op.
   *
   * We keep the original request's `key` so consecutive hero clicks
   * on the same row still re-trigger fly-to.
   */
  const normalisedFocusRequest = useMemo(() => {
    if (!focusRequest) return null;
    return { assetId: resolvePrimaryAssetId(focusRequest.assetId), key: focusRequest.key };
  }, [focusRequest, resolvePrimaryAssetId]);

  /**
   * External focus → same machinery as the marker click. We don't
   * just compare assetIds; the parent passes a `key` that increments
   * on every hero click, so clicking the same hero row twice still
   * re-pans the map and re-scrolls the list.
   */
  useEffect(() => {
    if (!focusRequest) return;
    handleSelectFromMap(focusRequest.assetId);
  }, [focusRequest, handleSelectFromMap]);

  const renderItem = useCallback(
    ({ item }: { item: WasteSenseSite }) => {
      const assetId = primaryAssetId(item);
      return (
        <View pb="sm">
          <PriorityMapSiteItem
            site={item}
            isActive={assetId === activeAssetId}
            isOpen={assetId === openAssetId}
            onToggle={handleToggle}
            onOpenDetails={onOpenSite}
          />
        </View>
      );
    },
    [activeAssetId, openAssetId, handleToggle, onOpenSite],
  );

  const keyExtractor = useCallback((item: WasteSenseSite) => primaryAssetId(item), []);

  return (
    <View gap="md">
      <Text textStyle="h4" color="text">
        Priority map
      </Text>

      <View flexDirection="row" gap="md" flexWrap="wrap">
        <View flex={1} minWidth={280} height={PRIORITY_MAP_HEIGHT}>
          <LegendList
            ref={listRef}
            data={sites as WasteSenseSite[]}
            renderItem={renderItem}
            keyExtractor={keyExtractor}
            extraData={`${activeAssetId ?? ""}|${openAssetId ?? ""}`}
            estimatedItemSize={ESTIMATED_ITEM_HEIGHT}
            recycleItems
            drawDistance={ESTIMATED_ITEM_HEIGHT * 4}
            showsVerticalScrollIndicator
            style={{ flex: 1 }}
          />
        </View>

        <View flex={2} minWidth={320} height={PRIORITY_MAP_HEIGHT}>
          <SiteLocationsMap
            sites={mapSites ?? []}
            priorityCountFor={priorityCountFor}
            focusAssetId={activeAssetId}
            focusRequest={normalisedFocusRequest}
            onSelectSite={handleSelectFromMap}
          />
        </View>
      </View>
    </View>
  );
}

interface PriorityMapSiteItemProps {
  site: WasteSenseSite;
  isActive: boolean;
  isOpen: boolean;
  onToggle: (assetId: string) => void;
  onOpenDetails?: (assetId: string) => void;
}

function PriorityMapSiteItem({ site, isActive, isOpen, onToggle, onOpenDetails }: PriorityMapSiteItemProps) {
  const assetId = primaryAssetId(site);
  const observations = site.observations ?? site.events ?? 0;
  const potentialIncidents = countPotentialSiteIncidents(site);
  const priorityIncidents = countPrioritySiteIncidents(site);

  return (
    <Card px="md" py="md" borderColor={isActive ? "danger" : "borderCard"}>
      <Pressable onPress={() => onToggle(assetId)} accessibilityRole="button" accessibilityState={{ expanded: isOpen }}>
        <View flexDirection="row" alignItems="center" gap="md">
          <View flex={1} gap="xxs">
            <Text textStyle="sm/semibold" color="text">
              {formatSiteHeading(site.title)}
            </Text>
          </View>
          <Badge count={priorityIncidents} />
        </View>
      </Pressable>

      {isOpen ?
        <View gap="xs" mt="md">
          <Divider />
          <Meta label="Units on site" value={String(site.assets.length || 1)} />
          <Meta label="Total observations" value={observations.toLocaleString()} />
          <Meta label="Potential incidents" value={potentialIncidents.toLocaleString()} />
          <Meta label="Priority incidents" value={priorityIncidents.toLocaleString()} />
          <Meta
            label="People · Vehicles"
            value={`${site.people.toLocaleString()} · ${site.vehicles.toLocaleString()}`}
          />

          {priorityIncidents > 0 ?
            <Text textStyle="xs/semibold" color="danger">
              {priorityIncidents.toLocaleString()} priority {priorityIncidents === 1 ? "incident" : "incidents"} in the
              7-day period
            </Text>
          : <Text textStyle="xs/regular" color="textMuted">
              No priority incidents in the 7-day period
            </Text>
          }

          {onOpenDetails ?
            <Pressable onPress={() => onOpenDetails(assetId)} accessibilityRole="button">
              <Text textStyle="xs/semibold" color="text">
                Open site details →
              </Text>
            </Pressable>
          : null}
        </View>
      : null}
    </Card>
  );
}

interface BadgeProps {
  count: number;
}

function Badge({ count }: BadgeProps) {
  const has = count > 0;
  return (
    <View bg={has ? "danger" : "muted"} px="sm" py="xxs" rounded={999} minWidth={28} alignItems="center">
      <Text textStyle="xs/semibold" color={has ? "textReversed" : "textMuted"}>
        {count.toLocaleString()}
      </Text>
    </View>
  );
}

interface MetaProps {
  label: string;
  value: string;
}

function Meta({ label, value }: MetaProps) {
  return (
    <View flexDirection="row" justifyContent="space-between" gap="md">
      <Text textStyle="xs/regular" color="textMuted">
        {label}
      </Text>
      <Text textStyle="xs/semibold" color="text">
        {value}
      </Text>
    </View>
  );
}

function Divider() {
  return <View h={1} bg="borderCard" />;
}

function primaryAssetId(site: WasteSenseSite): string {
  return site.assets[0] ?? site.title;
}
